import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TypingIndicatorService } from '../services/typingIndicatorService';

/**
 * Custom hook for managing typing indicators in a conversation
 *
 * @param conversationId - The ID of the conversation
 * @returns Functions to start and stop typing indicators
 *
 * @example
 * ```tsx
 * const { startTyping, stopTyping } = useTypingIndicator(conversationId);
 *
 * // In an input onChange handler:
 * const handleChange = (e) => {
 *   setValue(e.target.value);
 *   startTyping();
 * };
 *
 * // When sending a message:
 * const handleSend = async () => {
 *   await sendMessage(content);
 *   stopTyping();
 * };
 * ```
 */
export const useTypingIndicator = (conversationId: string) => {
  const { authState } = useAuth();
  const user = authState.user;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  /**
   * Start typing indicator
   * Automatically stops after 10 seconds of inactivity
   */
  const startTyping = useCallback(() => {
    if (!user || !conversationId) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only call the service if we're not already typing
    if (!isTypingRef.current) {
      TypingIndicatorService.startTyping(conversationId, user.uid);
      isTypingRef.current = true;
    }

    // Set timeout to auto-stop after 10 seconds
    timeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 10000);
  }, [conversationId, user]);

  /**
   * Stop typing indicator
   */
  const stopTyping = useCallback(() => {
    if (!user || !conversationId) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isTypingRef.current) {
      TypingIndicatorService.stopTyping(conversationId, user.uid);
      isTypingRef.current = false;
    }
  }, [conversationId, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (isTypingRef.current && user && conversationId) {
        TypingIndicatorService.stopTyping(conversationId, user.uid);
      }
    };
  }, [conversationId, user]);

  return {
    startTyping,
    stopTyping,
  };
};

/**
 * Custom hook for subscribing to typing indicators in a conversation
 *
 * @param conversationId - The ID of the conversation
 * @param callback - Function called when typing users change
 *
 * @example
 * ```tsx
 * const [typingUsers, setTypingUsers] = useState<string[]>([]);
 *
 * useTypingIndicatorSubscription(conversationId, (userIds) => {
 *   setTypingUsers(userIds);
 * });
 *
 * // Display:
 * {typingUsers.length > 0 && (
 *   <Typography variant="caption">
 *     {typingUsers.length === 1
 *       ? `${typingUsers[0]} is typing...`
 *       : `${typingUsers.length} people are typing...`
 *     }
 *   </Typography>
 * )}
 * ```
 */
export const useTypingIndicatorSubscription = (
  conversationId: string,
  callback: (userIds: string[]) => void
) => {
  useEffect(() => {
    if (!conversationId) return;

    const unsubscribe = TypingIndicatorService.subscribeToTyping(
      conversationId,
      callback
    );

    return () => unsubscribe();
  }, [conversationId, callback]);
};
